import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronDown, Loader2, Mail, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { phoneCountries } from "@/data/phone-countries";
import { PhoneCountry } from "@/data/types";
import { useUserAuth } from "@/contexts/UserAuthContext";
import { parseAndValidatePhone } from "@/security/phone";
import { Logo } from "@/components/shared/Logo";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { AlertCircle } from "lucide-react";
import { auth, db } from "@/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getPhoneOtpSentMessage } from "@/lib/phoneOtpCopy";
import { sendVerificationEmail } from "@/lib/sendVerificationEmail";

export default function Login() {
  const navigate = useNavigate();
  const {
    signInWithEmail,
    signInWithPhone,
    confirmPhoneCode,
    error,
    clearError,
    recaptchaContainerId,
  } = useUserAuth();

  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneStep, setPhoneStep] = useState<"number" | "code">("number");
  const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(
    phoneCountries[0]
  );
  const [countryPickerOpen, setCountryPickerOpen] = useState(false);

  const [emailForm, setEmailForm] = useState({ email: "", password: "" });
  const [phoneForm, setPhoneForm] = useState({ phone: "", code: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phoneSentHint, setPhoneSentHint] = useState<string | null>(null);
  const [isResendingCode, setIsResendingCode] = useState(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const newErrors: Record<string, string> = {};
    if (!emailForm.email.trim()) newErrors.email = "البريد الإلكتروني مطلوب";
    if (!emailForm.password) newErrors.password = "كلمة المرور مطلوبة";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setIsSubmitting(true);
    const ok = await signInWithEmail(emailForm.email, emailForm.password);
    setIsSubmitting(false);
    if (!ok) return;

    const current = auth.currentUser;
    if (current && !current.emailVerified) {
      const cooldownMs = 60_000;
      const currentEmail = current.email || emailForm.email;
      try {
        if (currentEmail) {
          const key = `emailVerificationResendAt:${currentEmail.toLowerCase()}`;
          const raw = localStorage.getItem(key);
          const ts = raw ? Number(raw) : NaN;
          const left = Number.isFinite(ts)
            ? cooldownMs - (Date.now() - ts)
            : 0;
          if (left > 0) {
            navigate("/verify-email", { replace: true });
            return;
          }
          localStorage.setItem(key, String(Date.now()));
        }
        await sendVerificationEmail({
          email: current.email || undefined,
          displayName: current.displayName || undefined,
        });
      } catch {
        // non-blocking
      }
      navigate("/verify-email", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  };

  const handlePhoneNumberSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const validation = parseAndValidatePhone(
      phoneForm.phone,
      selectedCountry.code,
      selectedCountry.dialCode
    );
    if (!validation.isValid) {
      setErrors({ phone: validation.error || "رقم الهاتف غير صحيح" });
      return;
    }
    setErrors({});
    setPhoneSentHint(null);
    const phoneNumber =
      validation.e164 ||
      `${selectedCountry.dialCode}${phoneForm.phone.replace(/\D/g, "")}`;
    setIsSubmitting(true);
    const ok = await signInWithPhone(phoneNumber);
    setIsSubmitting(false);
    if (ok) {
      setShowEmailLogin(false);
      setPhoneStep("code");
      setPhoneSentHint(getPhoneOtpSentMessage());
    }
  };

  const handleResendSmsCode = async () => {
    clearError();
    setPhoneSentHint(null);
    const validation = parseAndValidatePhone(
      phoneForm.phone,
      selectedCountry.code,
      selectedCountry.dialCode
    );
    if (!validation.isValid) {
      setErrors({ phone: validation.error || "رقم الهاتف غير صحيح" });
      setPhoneStep("number");
      return;
    }
    const phoneNumber =
      validation.e164 ||
      `${selectedCountry.dialCode}${phoneForm.phone.replace(/\D/g, "")}`;
    setIsResendingCode(true);
    const ok = await signInWithPhone(phoneNumber);
    setIsResendingCode(false);
    if (ok) {
      setPhoneSentHint(getPhoneOtpSentMessage());
    }
  };

  const handlePhoneCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    if (
      !phoneForm.code.trim() ||
      phoneForm.code.length < 4 ||
      phoneForm.code.length > 8
    ) {
      setErrors({ code: "أدخل رمز التحقق المرسل في الرسالة النصية (4–8 أرقام)" });
      return;
    }
    setErrors({});
    setIsSubmitting(true);
    const ok = await confirmPhoneCode(phoneForm.code.trim());
    setIsSubmitting(false);
    if (ok) {
      const u = auth.currentUser;
      if (u) {
        try {
          const ref = doc(db, "users", u.uid);
          const snap = await getDoc(ref);
          const existing = snap.data() as { countryCode?: string } | undefined;
          if (!existing?.countryCode) {
            await setDoc(
              ref,
              {
                countryCode: selectedCountry.code,
                updatedAt: new Date().toISOString(),
              },
              { merge: true },
            );
          }
        } catch {
          // non-blocking
        }
      }
      navigate("/", { replace: true });
    }
  };

  const openEmailLogin = () => {
    setShowEmailLogin(true);
    setErrors({});
    clearError();
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" dir="rtl">
      <div id={recaptchaContainerId} />
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent" />
        <div className="relative pt-12 pb-6 px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center shadow-lg">
            <Logo size="lg" showText={false} />
          </div>
          <h1 className="text-2xl font-bold text-foreground text-center mb-1">
            تسجيل الدخول
          </h1>
          <p className="text-muted-foreground text-sm text-center">
            أدخل رقم جوالك أو تابع بالبريد الإلكتروني
          </p>
        </div>
      </div>

      <div className="flex-1 px-6 pb-8">
        <Card className="max-w-md mx-auto border-2">
          <CardContent className="space-y-4 pt-6">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {phoneStep === "number" && (
              <div className="space-y-4">
                <form onSubmit={handlePhoneNumberSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone">رقم الجوال</Label>
                    <div
                      className={cn(
                        "flex items-center h-12 rounded-xl border-2 overflow-hidden",
                        errors.phone ? "border-destructive" : "border-border"
                      )}
                      dir="ltr"
                    >
                      <Popover
                        open={countryPickerOpen}
                        onOpenChange={setCountryPickerOpen}
                      >
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="flex items-center gap-2 h-full px-3 border-l border-border bg-muted/30"
                          >
                            <span className="text-lg">
                              {selectedCountry.flag}
                            </span>
                            <span className="text-sm font-semibold">
                              {selectedCountry.dialCode}
                            </span>
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[220px] p-0" align="start">
                          <div className="max-h-[260px] overflow-y-auto">
                            {phoneCountries.map((c) => (
                              <button
                                key={c.code}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(c);
                                  setCountryPickerOpen(false);
                                }}
                                className={cn(
                                  "flex items-center gap-3 w-full px-4 py-3 text-right hover:bg-primary/10",
                                  selectedCountry.code === c.code &&
                                    "bg-primary/15"
                                )}
                              >
                                <span className="text-lg">{c.flag}</span>
                                <span className="flex-1 text-sm">{c.nameAr}</span>
                                <span className="text-xs text-muted-foreground">
                                  {c.dialCode}
                                </span>
                                {selectedCountry.code === c.code && (
                                  <Check className="h-4 w-4 text-primary" />
                                )}
                              </button>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Input
                        id="login-phone"
                        type="tel"
                        placeholder="ادخل رقم الجوال"
                        value={phoneForm.phone}
                        onChange={(e) =>
                          setPhoneForm((p) => ({ ...p, phone: e.target.value }))
                        }
                        className="flex-1 border-0 focus-visible:ring-0 rounded-none"
                        disabled={isSubmitting}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-destructive text-xs">{errors.phone}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      سيتم إرسال رمز التحقق إلى رقمك في رسالة نصية
                    </p>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin ml-2" />
                        جاري إرسال رمز التحقق برسالة نصية...
                      </>
                    ) : (
                      "متابعة"
                    )}
                  </Button>
                </form>

                <div className="relative flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs text-muted-foreground shrink-0">أو</span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {!showEmailLogin ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 rounded-full border-2 gap-2"
                    onClick={openEmailLogin}
                  >
                    <Mail className="h-4 w-4 shrink-0" />
                    المتابعة بالبريد الإلكتروني
                  </Button>
                ) : null}

                {showEmailLogin && (
                  <form onSubmit={handleEmailSubmit} className="space-y-4 pt-1">
                    <div className="space-y-2">
                      <Label htmlFor="email">البريد الإلكتروني</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="example@email.com"
                        value={emailForm.email}
                        onChange={(e) =>
                          setEmailForm((p) => ({ ...p, email: e.target.value }))
                        }
                        className={cn(
                          errors.email && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="email"
                      />
                      {errors.email && (
                        <p className="text-destructive text-xs">{errors.email}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="password">كلمة المرور</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={emailForm.password}
                        onChange={(e) =>
                          setEmailForm((p) => ({
                            ...p,
                            password: e.target.value,
                          }))
                        }
                        className={cn(
                          errors.password && "border-destructive",
                          "rounded-xl"
                        )}
                        disabled={isSubmitting}
                        autoComplete="current-password"
                      />
                      {errors.password && (
                        <p className="text-destructive text-xs">
                          {errors.password}
                        </p>
                      )}
                    </div>
                    <div className="text-left">
                      <Link
                        to="/forgot-password"
                        className="text-sm text-primary hover:underline"
                      >
                        نسيت كلمة المرور؟
                      </Link>
                    </div>
                    <Button
                      type="submit"
                      className="w-full h-12 rounded-xl"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <Loader2 className="h-5 w-5 animate-spin ml-2" />
                      ) : (
                        "تسجيل الدخول"
                      )}
                    </Button>
                  </form>
                )}
              </div>
            )}

            {phoneStep === "code" && (
              <form onSubmit={handlePhoneCodeSubmit} className="space-y-4">
                {phoneSentHint && (
                  <p className="text-xs text-primary font-medium">{phoneSentHint}</p>
                )}
                <div className="space-y-2">
                  <Label htmlFor="code">أدخل رمز التحقق المرسل في الرسالة النصية</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="000000"
                    value={phoneForm.code}
                    onChange={(e) =>
                      setPhoneForm((p) => ({
                        ...p,
                        code: e.target.value.replace(/\D/g, ""),
                      }))
                    }
                    className={cn(errors.code && "border-destructive")}
                    disabled={isSubmitting}
                    autoComplete="one-time-code"
                  />
                  {errors.code && (
                    <p className="text-destructive text-xs">{errors.code}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 rounded-xl"
                  disabled={
                    isSubmitting ||
                    phoneForm.code.length < 4 ||
                    phoneForm.code.length > 8
                  }
                >
                  {isSubmitting ? (
                    <Loader2 className="h-5 w-5 animate-spin ml-2" />
                  ) : (
                    "تسجيل الدخول"
                  )}
                </Button>
                <button
                  type="button"
                  onClick={handleResendSmsCode}
                  disabled={isResendingCode || isSubmitting}
                  className="w-full text-sm text-primary hover:underline disabled:opacity-50"
                >
                  {isResendingCode
                    ? "جاري إرسال رمز التحقق برسالة نصية..."
                    : "إعادة إرسال الرمز برسالة نصية"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhoneStep("number");
                    setShowEmailLogin(false);
                    setPhoneSentHint(null);
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  تغيير رقم الهاتف
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          ما عندك حساب؟{" "}
          <Link
            to="/signup"
            className="text-primary font-medium hover:underline"
          >
            إنشاء حساب
          </Link>
        </p>
      </div>
    </div>
  );
}
